import type { CrudRepository } from '@/modules/shared/domain/CrudRepository';

interface Identificable<Id> {
  id: Id;
}

const getAll = async <Entity extends Identificable<Id>, Id>(key: string) => {
  const entities = sessionStorage.getItem(key) || '[]';

  return JSON.parse(entities) as Entity[];
};

const get = async <Entity extends Identificable<Id>, Id>(
  key: string,
  id: Id
): Promise<Entity | null> => {
  const entities = await getAll<Entity, Id>(key);
  const entity = entities.find((e) => e.id === id);

  return entity || null;
};

const save = async <Entity extends Identificable<Id>, Id>(
  key: string,
  entity: Omit<Entity, 'id'>
) => {
  const newEntity = { ...entity, id: Math.random() } as Entity;
  const entities = [...(await getAll(key)), newEntity];
  sessionStorage.setItem(key, JSON.stringify(entities));
  return newEntity;
};

const update = async <Entity extends Identificable<Id>, Id>(
  key: string,
  entity: Entity
) => {
  const entities = await getAll(key);
  const entitiesUpdated = entities.map((e) =>
    e.id === entity.id ? entity : e
  );
  sessionStorage.setItem(key, JSON.stringify(entitiesUpdated));

  return entity;
};

const remove = async <Entity extends Identificable<Id>, Id>(
  key: string,
  id: Id
): Promise<Entity | null> => {
  const entities = await getAll<Entity, Id>(key);
  const entity = entities.find((e) => e.id === id);

  sessionStorage.setItem(
    key,
    JSON.stringify(entities.filter((e) => e.id !== id))
  );

  return entity || null;
};

const updateAllElements = async <Entity extends Identificable<Id>, Id>(
  key: string,
  entities: Entity[]
) => {
  sessionStorage.setItem(key, JSON.stringify(entities));
  return entities;
};

export const sessionStorageRepository = <
  Entity extends Identificable<Id>,
  Id = string
>(
  key: string
): CrudRepository<Entity, Id> & {
  updateAllElements: (entities: Entity[]) => Promise<Entity[]>;
} => ({
  getAll: () => getAll<Entity, Id>(key),
  get: (id: Id) => get<Entity, Id>(key, id),
  save: (entity: Omit<Entity, 'id'>) => save<Entity, Id>(key, entity),
  remove: (id: Id) => remove<Entity, Id>(key, id),
  update: (entity: Entity) => update<Entity, Id>(key, entity),
  updateAllElements: (entities: Entity[]) => updateAllElements(key, entities)
});
